import React, { useState } from 'react'
import { useAuthStore } from '../hooks/useAuthStore'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'
import { DashboardCard } from '../components/DashboardCard'

// Mock profile data
const mockProfileData = {
  parent: {
    id: 1,
    display_name: 'คุณสมศรี',
    line_user_id: 'mock_line_user_id',
    phone: '081-234-5678',
    email: 'somsi@email.com',
    relationship: 'มารดา',
    emergency_contact: '082-345-6789',
    address: '123/45 หมู่บ้านสุขสันต์ ซอย 10 แขวงลาดพร้าว เขตลาดพร้าว กรุงเทพมหานคร 10230',
    joined_date: '2025-08-01'
  },
  student: {
    id: 1,
    name: 'เด็กหญิงใสใส สดใส',
    nickname: 'ใส',
    student_code: 'STU-001',
    birth_date: '2016-05-15',
    school: 'โรงเรียนประถมแห่งชาติ',
    grade: 'ป.3',
    session_group: 'main',
    subjects: ['คณิตศาสตร์', 'ภาษาไทย', 'ภาษาอังกฤษ'],
    enrolled_date: '2025-08-01',
    allergies: 'ไม่มี',
    medical_notes: 'ไม่มีโรคประจำตัว',
    emergency_contact_relation: 'บิดา',
    emergency_contact_phone: '082-345-6789'
  },
  preferences: {
    notifications: {
      payment_reminders: true,
      lesson_updates: true,
      announcements: true,
      expense_notifications: true
    },
    language: 'th',
    theme: 'light'
  },
  app_info: {
    version: '1.0.0',
    last_login: '2025-08-17T15:30:00.000Z',
    total_payments: 12,
    total_lessons: 45
  }
}

export const ProfilePage: React.FC = () => {
  const { logout } = useAuthStore()
  const [activeTab, setActiveTab] = useState<'parent' | 'student' | 'settings'>('parent')
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState(mockProfileData.parent)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [showSaveSuccess, setShowSaveSuccess] = useState(false)

  // Utility functions
  const formatThaiDate = (dateString: string) => {
    const date = new Date(dateString)
    const thaiYear = date.getFullYear() + 543
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    return `${date.getDate()} ${months[date.getMonth()]} ${thaiYear}`
  }

  const calculateAge = (birthDate: string) => {
    const today = new Date()
    const birth = new Date(birthDate)
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--
    }
    return age
  }

  const handleSaveProfile = () => {
    // Mock save action
    console.log('Saving profile:', editData)
    setShowSaveSuccess(true)
    setIsEditing(false)
    setTimeout(() => setShowSaveSuccess(false), 3000)
  }

  const handleLogout = () => {
    setShowLogoutConfirm(true)
  }

  const confirmLogout = () => {
    logout()
    setShowLogoutConfirm(false)
  }

  const cancelLogout = () => {
    setShowLogoutConfirm(false)
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader 
        title="โปรไฟล์"
        subtitle={`ข้อมูลส่วนตัวและนักเรียน`}
        actions={
          <button
            onClick={handleLogout}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ออกจากระบบ
          </button>
        }
      />
      
      <div className="px-4 py-6 space-y-6">
        {/* Success Message */}
        {showSaveSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-800 flex items-center">
              <span className="mr-2">✅</span>
              บันทึกข้อมูลสำเร็จ
            </p>
          </div>
        )}

        {/* Logout Confirmation */}
        {showLogoutConfirm && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h3 className="font-medium text-orange-900 mb-2">ยืนยันการออกจากระบบ</h3>
            <p className="text-sm text-orange-800 mb-4">คุณต้องการออกจากระบบหรือไม่?</p>
            <div className="flex gap-3">
              <button
                onClick={cancelLogout}
                className="btn-secondary flex-1"
              >
                ยกเลิก
              </button>
              <button
                onClick={confirmLogout}
                className="btn-primary bg-red-600 hover:bg-red-700 flex-1"
              >
                ออกจากระบบ
              </button>
            </div>
          </div>
        )}
        
        {/* Tab Navigation */}
        <div className="flex bg-white rounded-lg border p-1">
          <button
            onClick={() => setActiveTab('parent')}
            className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
              activeTab === 'parent'
                ? 'bg-purple-500 text-white'
                : 'text-gray-600 hover:text-purple-600'
            }`}
          >
            👤 ผู้ปกครอง
          </button>
          <button
            onClick={() => setActiveTab('student')}
            className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
              activeTab === 'student'
                ? 'bg-purple-500 text-white'
                : 'text-gray-600 hover:text-purple-600'
            }`}
          >
            🎓 นักเรียน
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
              activeTab === 'settings'
                ? 'bg-purple-500 text-white'
                : 'text-gray-600 hover:text-purple-600'
            }`}
          >
            ⚙️ ตั้งค่า
          </button>
        </div>

        {/* Parent Info Tab */}
        {activeTab === 'parent' && (
          <div className="space-y-4">
            <DashboardCard 
              title="👤 ข้อมูลผู้ปกครอง"
              className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200"
            >
              <div className="space-y-4">
                {!isEditing ? (
                  <>
                    <div className="text-center mb-6">
                      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-3xl">👤</span>
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {mockProfileData.parent.display_name}
                      </h2>
                      <p className="text-sm text-gray-600">
                        สมาชิกตั้งแต่ {formatThaiDate(mockProfileData.parent.joined_date)}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      <div className="bg-white rounded-lg border p-3">
                        <label className="text-sm font-medium text-gray-700">ความสัมพันธ์</label>
                        <p className="text-gray-900">{mockProfileData.parent.relationship}</p>
                      </div>
                      
                      <div className="bg-white rounded-lg border p-3">
                        <label className="text-sm font-medium text-gray-700">เบอร์โทรศัพท์</label>
                        <p className="text-gray-900">{mockProfileData.parent.phone}</p>
                      </div>
                      
                      <div className="bg-white rounded-lg border p-3">
                        <label className="text-sm font-medium text-gray-700">อีเมล</label>
                        <p className="text-gray-900">{mockProfileData.parent.email || 'ไม่ได้ระบุ'}</p>
                      </div>
                      
                      <div className="bg-white rounded-lg border p-3">
                        <label className="text-sm font-medium text-gray-700">เบอร์ติดต่อฉุกเฉิน</label>
                        <p className="text-gray-900">{mockProfileData.parent.emergency_contact}</p>
                      </div>
                      
                      <div className="bg-white rounded-lg border p-3">
                        <label className="text-sm font-medium text-gray-700">ที่อยู่</label>
                        <p className="text-gray-900 text-sm leading-relaxed">{mockProfileData.parent.address}</p>
                      </div>
                    </div>

                    <button
                      onClick={() => setIsEditing(true)}
                      className="btn-primary w-full"
                    >
                      ✏️ แก้ไขข้อมูล
                    </button>
                  </>
                ) : (
                  <>
                    <div className="space-y-4">
                      <div>
                        <label className="label-thai">เบอร์โทรศัพท์</label>
                        <input
                          type="tel"
                          value={editData.phone}
                          onChange={(e) => setEditData({...editData, phone: e.target.value})}
                          className="input-thai"
                        />
                      </div>
                      
                      <div>
                        <label className="label-thai">อีเมล</label>
                        <input
                          type="email"
                          value={editData.email}
                          onChange={(e) => setEditData({...editData, email: e.target.value})}
                          className="input-thai"
                        />
                      </div>
                      
                      <div>
                        <label className="label-thai">เบอร์ติดต่อฉุกเฉิน</label>
                        <input
                          type="tel"
                          value={editData.emergency_contact}
                          onChange={(e) => setEditData({...editData, emergency_contact: e.target.value})}
                          className="input-thai"
                        />
                      </div>
                      
                      <div>
                        <label className="label-thai">ที่อยู่</label>
                        <textarea
                          value={editData.address}
                          onChange={(e) => setEditData({...editData, address: e.target.value})}
                          className="input-thai min-h-[100px]"
                          rows={4}
                        />
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => setIsEditing(false)}
                        className="btn-secondary flex-1"
                      >
                        ยกเลิก
                      </button>
                      <button
                        onClick={handleSaveProfile}
                        className="btn-primary flex-1"
                      >
                        บันทึก
                      </button>
                    </div>
                  </>
                )}
              </div>
            </DashboardCard>
          </div>
        )}

        {/* Student Info Tab */}
        {activeTab === 'student' && (
          <div className="space-y-4">
            <DashboardCard 
              title="🎓 ข้อมูลนักเรียน"
              className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
            >
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-3xl">🎓</span>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {mockProfileData.student.name}
                  </h2>
                  <p className="text-sm text-gray-600">
                    ชื่อเล่น: {mockProfileData.student.nickname}
                  </p>
                  <p className="text-xs text-gray-500">
                    รหัสนักเรียน: {mockProfileData.student.student_code}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white rounded-lg border p-3">
                    <label className="text-sm font-medium text-gray-700">วันเกิด</label>
                    <p className="text-gray-900">
                      {formatThaiDate(mockProfileData.student.birth_date)} 
                      <span className="text-sm text-gray-600 ml-2">
                        (อายุ {calculateAge(mockProfileData.student.birth_date)} ปี)
                      </span>
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg border p-3">
                    <label className="text-sm font-medium text-gray-700">โรงเรียน</label>
                    <p className="text-gray-900">{mockProfileData.student.school}</p>
                  </div>
                  
                  <div className="bg-white rounded-lg border p-3">
                    <label className="text-sm font-medium text-gray-700">ระดับชั้น</label>
                    <p className="text-gray-900">{mockProfileData.student.grade}</p>
                  </div>
                  
                  <div className="bg-white rounded-lg border p-3">
                    <label className="text-sm font-medium text-gray-700">กลุ่มเรียน</label>
                    <p className="text-gray-900">{mockProfileData.student.session_group}</p>
                  </div>
                  
                  <div className="bg-white rounded-lg border p-3">
                    <label className="text-sm font-medium text-gray-700">วิชาที่เรียน</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {mockProfileData.student.subjects.map((subject, index) => (
                        <span key={index} className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                          {subject}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg border p-3">
                    <label className="text-sm font-medium text-gray-700">เริ่มเรียน</label>
                    <p className="text-gray-900">{formatThaiDate(mockProfileData.student.enrolled_date)}</p>
                  </div>
                  
                  <div className="bg-white rounded-lg border p-3">
                    <label className="text-sm font-medium text-gray-700">ประวัติการแพ้</label>
                    <p className="text-gray-900">{mockProfileData.student.allergies}</p>
                  </div>
                  
                  <div className="bg-white rounded-lg border p-3">
                    <label className="text-sm font-medium text-gray-700">หมายเหตุทางการแพทย์</label>
                    <p className="text-gray-900">{mockProfileData.student.medical_notes}</p>
                  </div>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <h4 className="font-medium text-orange-900 mb-2">ข้อมูลติดต่อฉุกเฉิน</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-orange-800">ความสัมพันธ์:</span> {mockProfileData.student.emergency_contact_relation}</p>
                    <p><span className="text-orange-800">เบอร์โทร:</span> {mockProfileData.student.emergency_contact_phone}</p>
                  </div>
                </div>
              </div>
            </DashboardCard>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-4">
            <DashboardCard 
              title="🔔 การแจ้งเตือน"
              className="bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200"
            >
              <div className="space-y-4">
                {Object.entries(mockProfileData.preferences.notifications).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-gray-700">
                      {key === 'payment_reminders' && '💳 การแจ้งเตือนการชำระเงิน'}
                      {key === 'lesson_updates' && '📚 อัพเดตบทเรียน'}
                      {key === 'announcements' && '📢 ประกาศจากโรงเรียน'}
                      {key === 'expense_notifications' && '🧾 การแจ้งเตือนค่าใช้จ่าย'}
                    </span>
                    <div className={`w-12 h-6 rounded-full p-1 transition-colors ${
                      value ? 'bg-green-500' : 'bg-gray-300'
                    }`}>
                      <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
                        value ? 'translate-x-6' : 'translate-x-0'
                      }`} />
                    </div>
                  </div>
                ))}
              </div>
            </DashboardCard>

            <DashboardCard 
              title="📱 ข้อมูลแอป"
              className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200"
            >
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{mockProfileData.app_info.total_payments}</p>
                    <p className="text-xs text-gray-600">ครั้งที่ชำระเงิน</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{mockProfileData.app_info.total_lessons}</p>
                    <p className="text-xs text-gray-600">บทเรียนที่ผ่านมา</p>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">เวอร์ชันแอป:</span>
                    <span className="font-medium">{mockProfileData.app_info.version}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">เข้าสู่ระบบล่าสุด:</span>
                    <span className="font-medium">{formatThaiDate(mockProfileData.app_info.last_login)}</span>
                  </div>
                </div>
              </div>
            </DashboardCard>

            <DashboardCard 
              title="⚙️ การตั้งค่าระบบ"
              className="bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200"
            >
              <div className="space-y-4">
                <button className="btn-secondary w-full text-left">
                  🔄 อัพเดตข้อมูลจาก LINE
                </button>
                <button className="btn-secondary w-full text-left">
                  📞 ติดต่อสนับสนุน
                </button>
                <button className="btn-secondary w-full text-left">
                  📋 นโยบายความเป็นส่วนตัว
                </button>
                <button className="btn-secondary w-full text-left">
                  📄 ข้อกำหนดการใช้งาน
                </button>
                <button 
                  onClick={handleLogout}
                  className="btn-primary w-full bg-red-500 hover:bg-red-600 border-red-500"
                >
                  🚪 ออกจากระบบ
                </button>
              </div>
            </DashboardCard>
          </div>
        )}
      </div>
      
      <TabNavigation />
    </div>
  )
}